import { Stack } from '@mui/material';
import { EditorEvents } from '@provision/rich-text-editor';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { RichTextField } from 'src/components/Forms/RichTextField/rich-text-field';
import { usePartnerGroups } from 'src/graphql';
import { PartnerTabContactPartner } from './partner-contact-info';

interface NotesTabProps {
  partner: PartnerTabContactPartner;
  onFieldChange: (field: string, value: string) => void;
  readonly: boolean;
}

export const NotesTab = ({ partner, onFieldChange, readonly }: NotesTabProps) => {
  const partnerDetails = partner?.partnerOnesheet?.[0];

  const { partnerGroups, loading: partnerGroupsLoading } = usePartnerGroups({
    partnerId: partner?.partnerId,
  });

  const platformAccess = useMemo(
    () =>
      partnerGroups
        ?.filter((partnerGroup) => !!partnerGroup.description)
        .map(
          (partnerGroup) =>
            `<h4><u>${partnerGroup.name}</u></h4>
      ${partnerGroup.description}`,
        )
        .join(''),
    [partnerGroups],
  );

  const fields = useMemo(() => {
    if (isNullish(partner?.partnerId)) {
      return [];
    }

    return [
      {
        label: 'Internal Partner Notes',
        editorOptions: {
          editable: !readonly,
          onUpdate: ({ editor }: EditorEvents['update']) => {
            onFieldChange('internalSupportNotes', editor.getHTML());
          },
          content: partnerDetails?.internalSupportNotes ?? '',
        },
        dependencies: [partner.partnerId],
      },
      {
        label: 'Escalation Policy',
        editorOptions: {
          editable: !readonly,
          onUpdate: ({ editor }: EditorEvents['update']) => {
            onFieldChange('escalationPolicy', editor.getHTML());
          },
          content: partnerDetails?.escalationPolicy ?? '',
        },
        dependencies: [partner.partnerId],
      },
      {
        label: 'Platform Access',
        loading: partnerGroupsLoading,
        editorOptions: {
          editable: false,
          content: platformAccess,
        },
        dependencies: [platformAccess],
      },
    ];
  }, [
    readonly,
    partner.partnerId,
    partnerDetails?.escalationPolicy,
    partnerDetails?.internalSupportNotes,
    partnerGroupsLoading,
    platformAccess,
    onFieldChange,
  ]);

  return (
    <Stack spacing={2} sx={{ paddingTop: 2 }}>
      {fields.map((field) => (
        <RichTextField
          key={field.label}
          label={field.label}
          loading={field.loading}
          editorOptions={field.editorOptions}
          dependencies={field.dependencies}
        />
      ))}
    </Stack>
  );
};
